/**
 * Hosted Twilio Flex webchat (live-webchat iframe) for SH live chat (CR-415). Same embed as
 * axa-web-app (CR-133): a MessageChannel handshake, then webchat:init over the port.
 * Message shapes mirror live-webchat/src/contracts/embed.ts.
 *
 * Held at module level rather than in a component: the iframe lives on document.body,
 * ConfirmQuestion (the "Open live chat" button) is destroyed on every question change, and
 * App.vue shows or hides the chat on route changes, so a component-owned channel would
 * die mid-conversation.
 */
import axios from "axios";
import * as Sentry from "@sentry/vue";
import config from "@/config/constants";

// Keep in step with WEBCHAT_SCHEMA_VERSION in live-webchat/src/contracts/embed.ts.
export const WEBCHAT_SCHEMA_VERSION = 1;

// Must stay in sync with the #twilio-webchat-iframe styles in App.vue.
export const WEBCHAT_IFRAME_ID = "twilio-webchat-iframe";

// Backend feature flag (api/u/feature-flag). On: live chat uses this webchat. Off: the
// legacy MessageBird widget.
export const WEBCHAT_FLAG = "sh_eap_live_chat_web";

// A handshake that never completes (dead network, wrong host, unresponsive iframe) is
// reported as a failure rather than left spinning.
const CONNECT_TIMEOUT_MS = 20000;

// Clear the bottom-right Intercom launcher: its 48px button sits 20px off both edges, so
// 20 + 48 + a 16px gap. Right matches Intercom's 20px so the two line up.
const OFFSET_BOTTOM = 84;
const OFFSET_RIGHT = 20;

let flagRequest = null;
let session = null;
let signOutCount = 0;

// Lets an async caller tell whether a logout happened while it was waiting. Logout signs
// the webchat out before the user is cleared, so the store alone can't tell.
export const webchatSignOutCount = () => signOutCount;

/**
 * Whether this user gets the hosted webchat. Cached per user for the page's lifetime so
 * a click doesn't wait on the network. A failed check means MessageBird, the safe default.
 */
export const isWebchatEnabled = userId => {
  if (flagRequest && flagRequest.userId === userId) return flagRequest.promise;

  const promise = axios
    .post(
      config.api_env + "/application/api/u/feature-flag",
      { user_id: userId, feature_flags: [WEBCHAT_FLAG] },
      config.options
    )
    .then(res =>
      Boolean(res.data.success && res.data.feature_flags[WEBCHAT_FLAG])
    )
    .catch(e => {
      // eslint-disable-next-line no-console
      console.error("Webchat feature-flag check failed", e);
      if (flagRequest && flagRequest.promise === promise) flagRequest = null;
      return false;
    });
  flagRequest = { userId, promise };
  return promise;
};

const report = (message, tags, extra) => {
  // eslint-disable-next-line no-console
  console.error(message);
  Sentry.captureMessage(message, { level: "error", tags, extra });
};

// Drop a session's channel and listeners. The iframe is the caller's call: removed on
// failure, kept (hidden) on logout so the logout message can still land.
const release = s => {
  window.removeEventListener("message", s.listener);
  clearTimeout(s.timer);
  if (s.port) {
    s.port.onmessage = null;
    s.port.close();
  }
  if (session === s) session = null;
};

// Ended deliberately (logout, another user): not an error, so anyone still waiting on it
// stops waiting without being shown one.
const supersede = s => {
  release(s);
  s.resolve();
};

const fail = (s, message, tags, extra) => {
  report(message, tags, extra);
  release(s);
  s.iframe.remove();
  s.reject(new Error(message));
};

const settle = s => {
  clearTimeout(s.timer);
  s.resolve();
};

const onPortMessage = (s, message) => {
  if (!message || typeof message !== "object") return;
  if (message.v !== WEBCHAT_SCHEMA_VERSION) return;
  switch (message.type) {
    case "webchat:expanded":
      // CSS picks floating vs full-screen (see .expanded in App.vue); JS just toggles.
      s.iframe.classList.add("expanded");
      settle(s);
      break;
    case "webchat:collapsed":
      s.iframe.classList.remove("expanded");
      settle(s);
      break;
    case "webchat:error":
      // Couldn't boot (bad token, unprovisioned region, outage): take the dead iframe down
      // rather than leave an invisible overlay.
      fail(
        s,
        `Twilio webchat failed to boot: ${message.code || "unknown"}`,
        { stage: "webchat_error", code: message.code || "unknown" },
        { message: message.message }
      );
      break;
  }
};

// Opens a private MessageChannel and sends init over it, so the token never crosses the
// broadcast window surface.
const openChannel = s => {
  if (!s.iframe.contentWindow) return;
  if (s.port) {
    s.port.onmessage = null;
    s.port.close();
  }
  const channel = new MessageChannel();
  s.port = channel.port1;
  channel.port1.onmessage = event => onPortMessage(s, event.data);
  s.iframe.contentWindow.postMessage(
    { type: "webchat:connect", v: WEBCHAT_SCHEMA_VERSION },
    s.origin,
    [channel.port2]
  );
  // The token is the SL-issued session token (user_id getter): validated server-side by
  // the webchat, never sent to Twilio. autoOpen: the chat only loads from a button click,
  // so it should come up open.
  channel.port1.postMessage({
    type: "webchat:init",
    v: WEBCHAT_SCHEMA_VERSION,
    token: s.token,
    displayMode: "widget",
    autoOpen: true,
    apiBaseUrl: new URL(config.api_env).origin,
    region: config.webchat_region
  });
};

const createSession = token => {
  // A previous user's iframe, hidden by signOutWebchat, would otherwise share the id.
  const stale = document.getElementById(WEBCHAT_IFRAME_ID);
  if (stale) stale.remove();

  const webchatUrl = new URL(config.webchat_url);
  webchatUrl.searchParams.set("offsetBottom", String(OFFSET_BOTTOM));
  webchatUrl.searchParams.set("offsetRight", String(OFFSET_RIGHT));

  const iframe = document.createElement("iframe");
  iframe.id = WEBCHAT_IFRAME_ID;
  iframe.src = webchatUrl.toString();
  iframe.title = "Live chat";
  // allow-scripts + allow-same-origin are both needed (an opaque origin breaks the SDK's
  // localStorage); the value is in what's withheld, above all allow-top-navigation.
  // popups/downloads carry links and attachments in a conversation.
  iframe.setAttribute(
    "sandbox",
    "allow-scripts allow-same-origin allow-forms allow-popups allow-downloads"
  );
  iframe.setAttribute("allow", "clipboard-write");

  const s = {
    iframe,
    origin: webchatUrl.origin,
    token,
    port: null
  };
  s.connected = new Promise((resolve, reject) => {
    s.resolve = resolve;
    s.reject = reject;
  });

  // Only webchat:ready arrives over the window: it's the signal to open the channel, so it
  // can't travel over a port that doesn't exist yet. Everything else uses the port.
  s.listener = event => {
    if (event.origin !== s.origin) return;
    const message = event.data;
    if (!message || typeof message !== "object") return;
    if (
      message.type !== "webchat:ready" ||
      message.v !== WEBCHAT_SCHEMA_VERSION
    )
      return;
    if (typeof message.offsetBottom === "number") {
      iframe.style.setProperty(
        "--webchat-offset-bottom",
        `${message.offsetBottom}px`
      );
    }
    if (typeof message.offsetRight === "number") {
      iframe.style.setProperty(
        "--webchat-offset-right",
        `${message.offsetRight}px`
      );
    }
    openChannel(s);
  };
  window.addEventListener("message", s.listener);

  s.timer = setTimeout(
    () =>
      fail(s, "Twilio webchat did not connect in time", {
        stage: "webchat_timeout"
      }),
    CONNECT_TIMEOUT_MS
  );

  document.body.appendChild(iframe);
  return s;
};

/**
 * Open the webchat for this user from the "Open live chat" button, creating it if needed.
 * Resolves once the widget is up; rejects if the config is missing or it can't load or
 * authenticate, already reported to Sentry, so callers only show the error.
 */
export const openWebchat = token => {
  // A different user on the same page (e.g. a handover without a logout) must not reuse
  // the previous user's channel. The webchat's own owner check clears its storage too.
  if (session && session.token !== token) {
    const previous = session;
    supersede(previous);
    previous.iframe.remove();
  }

  // Same user: reuse the warm iframe, which keeps its conversation. There's no contract
  // message to open a booted widget, so a collapsed one stays on its launcher.
  if (session) {
    session.iframe.style.display = "block";
    return session.connected;
  }

  if (!config.webchat_region || !config.webchat_url) {
    const message =
      "Not opening Twilio webchat: VUE_APP_REGION or VUE_APP_WEBCHAT_URL missing/invalid";
    report(message, { stage: "webchat_config" });
    return Promise.reject(new Error(message));
  }

  session = createSession(token);
  return session.connected;
};

/** Hide or re-show an open webchat without touching its session (route changes). */
export const setWebchatVisible = visible => {
  if (!session) return;
  session.iframe.style.display = visible ? "block" : "none";
};

/**
 * Tell the embedded webchat its session has ended, then hide it. Called from the Vuex
 * logout action so every logout path gets it.
 *
 * The SDK persists conversation state to localStorage on the webchat's own origin, which
 * survives iframe removal and a full page navigation, so without this the next person on
 * a shared machine could resume the previous user's conversation. A plain postMessage to
 * the pinned origin, so it works with no port open.
 *
 * Best-effort: postMessage is async, so a logout that navigates immediately can cut the
 * frame off first. Hidden rather than removed, to keep it alive as long as possible.
 */
export const signOutWebchat = () => {
  signOutCount += 1;
  flagRequest = null;
  if (session) supersede(session);

  const iframe = document.getElementById(WEBCHAT_IFRAME_ID);
  if (!iframe || !iframe.contentWindow || !config.webchat_url) return;

  try {
    iframe.contentWindow.postMessage(
      { type: "webchat:logout", v: WEBCHAT_SCHEMA_VERSION },
      new URL(config.webchat_url).origin
    );
  } catch {
    // Unparseable webchat_url: nothing to signal, and logout must not fail on it.
  }

  iframe.classList.remove("expanded");
  iframe.style.display = "none";
};
